<template>
  <section class="zohoMea-section partner-view-section" v-if="template?.trim() !== 'Incident Management'">
    <div class="container">
      <div class="slider-client">
        <div class="client-head" style="margin-bottom: -20px !important;">
          <h2 data-aos="fade-up" data-aos-delay="100">
            {{ content.heading }}
            <span>{{ content.headingHighlight }}</span>
          </h2>
        </div>
        <div class="customer-logos slider">
          <div class="slide" v-for="logo in logos" :key="logo.file">
            <img
              loading="lazy"
              decoding="async"
              width="154"
              height="100"
              :src="logoUrl(logo.file)"
              :alt="logo.alt"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
// F6: this markup used to be ~150 lines of hand-written <div class="slide">
// blocks — 29 of them in the default set alone, each repeating the full CDN URL.
// Adding or removing a client logo meant editing markup in five places, and the
// four template-specific sets had all drifted to alt="Client 1" on every image.
// The list is data now; the markup is one v-for.
import homeContent from '@/assets/home-content.json';

// Fallback base, used only if a caller passes a content block without one.
const LOGO_BASE = 'https://measiteassetpublic.zohostratus.in/img/client/';

// The default strip moved to assets/home-content.json — it is the home page's
// content, and that is where it is edited now. These per-solution sets stayed
// here: they belong to the solution pages, not to the home page, and a file
// named home-content.json is the wrong place to look for them.
const LOGO_SETS = {
  'Project Management': [
    { file: 'Khimji Ramdas Frame (2).png', alt: 'Khimji Ramdas' },
    { file: 'SEC Frame (2).png', alt: 'SEC' },
  ],
  'Loyalty Management': [
    { file: 'Peets Coffee Frame (2).png', alt: "Peet's Coffee" },
    { file: 'Krispy Kreme Frame (2).png', alt: 'Krispy Kreme' },
    { file: 'Aroya Cruise Frame (2).png', alt: 'Aroya Cruise' },
  ],
  'Procurement': [
    { file: 'NSG Frame (2).png', alt: 'NSG' },
    { file: 'Khimji Ramdas Frame (2).png', alt: 'Khimji Ramdas' },
    { file: 'Meedaf Frame (2).png', alt: 'Meedaf' },
    { file: 'Qadsiah Frame (2).png', alt: 'Qadsiah' },
  ],
  'Fitness Platform': [
    { file: 'Fitness First Frame (2).png', alt: 'Fitness First' },
    { file: 'Vogue Frame (2).png', alt: 'Vogue' },
  ],
  'Automotive Management': [
    { file: 'Eastvaal Motors Frame (2).png', alt: 'Eastvaal Motors' }
  ],
};

export default {
  name: "HomePatnerViews",
  props: {
    template: String,
    // Defaulted, because this section is also rendered by four solution hero
    // components that know nothing about the home page's content file.
    content: {
      type: Object,
      default: () => homeContent.HomePatnerViews
    }
  },
  computed: {
    // A named solution shows its own short set; everything else — the home page
    // included — shows the strip from the content block.
    logos() {
      return LOGO_SETS[(this.template || '').trim()]
        || this.content.logos
        || [];
    },
  },
  methods: {
    logoUrl(file) {
      // encodeURI, not encodeURIComponent: some filenames contain spaces and
      // parentheses, and this reproduces exactly what the browser already did
      // with the previously hardcoded raw URLs — escape the spaces, leave the
      // parentheses alone. encodeURIComponent would also escape ( and ) and
      // change the request path the CDN sees.
      return encodeURI((this.content.logoBaseUrl || LOGO_BASE) + file);
    },
  },
};
</script>

<style>
.partner-view-section {
  margin-top: -120px;
}

/* F6: reserve the row's height before Slick initialises.
   slider.css sizes the images with `.slick-slide img { width:100%; height:100px }`,
   but that class only exists once Slick has run. Until then the slides are
   unsized, so a strip of 29 images that are still loading — or that failed with
   the CDN's 503s — collapses the row and then shoves the page down when they
   arrive. Matching the sizing here means the space is held from first paint. */
.customer-logos .slide img {
  width: 100%;
  height: 100px;
}

/* Cap the strip to a single row.
   The rule above sizes the images, which is necessary but not sufficient:
   custom.js initialises Slick on this container while the page is still
   laying out, so it can measure a zero width, derive a zero track width, and
   leave all 64 slides (29 originals plus 35 clones) stacked one per row
   instead of floated into a line.

   Measured on the live site, the document height went 940px -> 9280px ->
   2854px across that window, and that one collapse is 99.6% of the whole
   page's layout shift: 0.1152 of a total CLS of 0.1157, against a budget of
   0.1. Capping the height here means a carousel that measured itself wrongly
   overflows invisibly instead of shoving every section below it down and then
   yanking it back up.

   160px is one row (a 100px logo in a 102px slide inside slick-list's
   padding settles at 142px) plus headroom, so it never clips a correct
   layout at any breakpoint. */
.customer-logos {
  max-height: 160px;
  overflow: hidden;
}
</style>
